import { Suspense, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Html, RoundedBox } from "@react-three/drei";
import { MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SPOTS } from "@/lib/aiot";

const B = ({ s, p, c, r = 0.04 }) => <RoundedBox args={s} position={p} radius={r} smoothness={2} castShadow receiveShadow><meshStandardMaterial color={c} roughness={0.7} /></RoundedBox>;

export const Flat = () => (
  <group>
    <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow><planeGeometry args={[8, 6]} /><meshStandardMaterial color="#E7D8C4" roughness={0.85} /></mesh>
    <mesh position={[0, 1.5, -3]} receiveShadow><boxGeometry args={[8, 3, 0.08]} /><meshStandardMaterial color="#F1F5F9" /></mesh>
    <mesh position={[-4, 1.5, 0]} receiveShadow><boxGeometry args={[0.08, 3, 6]} /><meshStandardMaterial color="#E2E8F0" /></mesh>
    <mesh position={[1.6, 1.5, -1.35]} receiveShadow><boxGeometry args={[0.1, 3, 3.3]} /><meshStandardMaterial color="#F8FAFC" /></mesh>
    <mesh position={[-3.96, 1.05, 0.9]}><boxGeometry args={[0.04, 2.1, 1]} /><meshStandardMaterial color="#8B5E3C" /></mesh>
    <mesh position={[-1.2, 1.6, -2.95]}><boxGeometry args={[1.6, 1.2, 0.03]} /><meshStandardMaterial color="#BAE6FD" emissive="#7DD3FC" emissiveIntensity={0.25} /></mesh>
    <B s={[1.8, 0.06, 0.25]} p={[-1.2, 0.95, -2.85]} c="#CBD5E1" r={0.02} />
    <B s={[1.4, 0.05, 0.3]} p={[0.35, 1.85, -2.82]} c="#A16207" r={0.02} />
    <B s={[2.2, 0.45, 0.9]} p={[-1.2, 0.25, -0.6]} c="#334155" />
    <B s={[2.2, 0.5, 0.25]} p={[-1.2, 0.6, -1.0]} c="#475569" />
    <B s={[0.9, 0.4, 0.6]} p={[-1.2, 0.2, 0.7]} c="#D6A86B" />
    <B s={[1.1, 0.32, 0.22]} p={[-2.6, 2.5, -2.85]} c="#FFFFFF" r={0.06} />
    <B s={[2.2, 0.9, 0.6]} p={[2.9, 0.45, -2.68]} c="#CBD5E1" />
    <B s={[2.3, 0.05, 0.66]} p={[2.9, 0.92, -2.68]} c="#1F2937" r={0.01} />
    <mesh position={[2.4, 0.93, -2.65]}><boxGeometry args={[0.5, 0.02, 0.38]} /><meshStandardMaterial color="#94A3B8" metalness={0.8} roughness={0.2} /></mesh>
    <mesh position={[3.35, 0.95, -2.65]}><cylinderGeometry args={[0.12, 0.12, 0.03, 24]} /><meshStandardMaterial color="#111827" /></mesh>
    <mesh position={[-1, 2.98, -0.3]}><cylinderGeometry args={[0.18, 0.18, 0.03, 24]} /><meshStandardMaterial color="#FEF9C3" emissive="#FDE68A" emissiveIntensity={0.6} /></mesh>
  </group>
);

const Spot = ({ k, p, picked, label, onPick }) => (
  <group position={p}>
    <mesh onClick={(e) => { e.stopPropagation(); onPick(k); }} onPointerOver={() => { document.body.style.cursor = "pointer"; }} onPointerOut={() => { document.body.style.cursor = ""; }}>
      <sphereGeometry args={[picked ? 0.16 : 0.12, 24, 24]} />
      <meshStandardMaterial color={picked ? "#059669" : "#0EA5E9"} emissive={picked ? "#10B981" : "#38BDF8"} emissiveIntensity={picked ? 1.2 : 0.6} transparent opacity={0.9} />
    </mesh>
    {picked && <Html center distanceFactor={9} position={[0, 0.35, 0]}><div className="whitespace-nowrap rounded-full bg-[#059669] text-white text-[11px] font-bold px-2.5 py-1 shadow">{label}</div></Html>}
  </group>
);

export const PlaceRoom = ({ item, value, onChange }) => {
  const { L, lang } = useLang();
  const keys = useMemo(() => Object.keys(SPOTS), []);
  const label = (k) => (lang === "hi" ? SPOTS[k].hi : SPOTS[k].en);
  return (
    <div data-testid="test-place-task">
      <div className="rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-b from-[#EFF6FF] to-white h-[320px] sm:h-[380px]">
        <Canvas shadows dpr={[1, 1.5]} camera={{ position: [5.5, 5, 6.5], fov: 45 }}>
          <ambientLight intensity={0.7} />
          <directionalLight position={[4, 8, 5]} intensity={1.6} castShadow shadow-mapSize={[1024, 1024]} />
          <Suspense fallback={null}>
            <Flat />
            {keys.map((k) => <Spot key={k} k={k} p={SPOTS[k].p} picked={value === k} label={label(k)} onPick={onChange} />)}
          </Suspense>
          <OrbitControls target={[0, 1, -1]} enablePan={false} minDistance={5} maxDistance={13} maxPolarAngle={Math.PI / 2.15} />
        </Canvas>
      </div>
      <p className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{L(`Drag to look around. Tap a blue spot or pick from the list to place the ${item.device}.`, `घुमाकर देखिए। ${item.device_hi || item.device} रखने के लिए नीला पॉइंट tap करें या लिस्ट से चुनें।`)}</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {keys.map((k) => (
          <button key={k} type="button" onClick={() => onChange(k)} className={`text-left rounded-xl border-2 px-3 py-2 text-sm font-semibold transition-colors ${value === k ? "border-[#059669] bg-[#ECFDF5] text-[#065F46]" : "border-slate-200 bg-white hover:border-[#14B8A6]"}`} data-testid={`test-place-${k}`}>{label(k)}</button>
        ))}
      </div>
    </div>
  );
};
